import React, { useMemo } from "react";
import { Card, CardBody, Heading, Badge, Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@aidkitorg/component-library";
import type { TargetFieldsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/targetFieldsWorkflow";
import WorkflowReview from "./WorkflowReview";

export default function TargetFieldsWorkflowReview({ distroCommitId }: { distroCommitId: string }) {
  
  const renderTargetFieldsResults = (workflow: any) => {
    if (!workflow?.result?.success) {
      return null;
    }

    const result = workflow.result as TargetFieldsWorkflowResult;

    return <TargetFieldsResultsContent result={result} />;
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="target_fields"
      title="Target Fields Analysis"
      renderSummary={renderTargetFieldsResults}
    />
  );
}

function TargetFieldsResultsContent({ result }: { result: TargetFieldsWorkflowResult }) {
  // Memoize the filtered arrays to avoid recalculating on every render
  const fieldsWithChanges = useMemo(() => 
    result.targetFields?.filter(field => field.countChangedApplicants > 0) || [], 
  [result.targetFields]
  );

  const fieldsWithoutChanges = useMemo(() => 
    result.targetFields?.filter(field => field.countChangedApplicants === 0).sort((a, b) => a.key.localeCompare(b.key)) || [], 
  [result.targetFields]
  );

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card className="max-w-5xl">
        <CardBody>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="font-medium">Fields Affected</dt>
              <dd><span className="font-bold text-orange-600">{fieldsWithChanges.length}</span> of {result.targetFields?.length || 0}</dd>
            </div>
            <div>
              <dt className="font-medium">Applicants Affected</dt>
              <dd><span className="font-bold text-orange-600">{result.changedApplicants.toLocaleString()}</span> of {result.totalApplicants.toLocaleString()}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {/* Field Changes Details */}
      {fieldsWithChanges.length > 0 && (
        <Card className="max-w-5xl">
          <CardBody>
            <Heading level="h3" className="text-md font-semibold mb-4" id="field-changes-details">
              Value Changes
            </Heading>
            <div className="space-y-3">
              {fieldsWithChanges.map((field) => {
                const percentageImpact = result.totalApplicants > 0 
                  ? Math.round((field.countChangedApplicants / result.totalApplicants) * 100)
                  : 0;
                  
                return (
                  <div 
                    key={field.key}
                    className="border rounded-2xl overflow-hidden border-amber-200"
                  >
                    {/* Field Header - Always Visible */}
                    <div className="flex justify-between items-center px-4 py-3 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-md">{field.key}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-xl">
                          {field.countChangedApplicants.toLocaleString()}
                          <span className="text-xs text-gray-600 ml-1">
                            ({percentageImpact}%)
                          </span>
                        </span>
                        <div className="text-xs text-gray-500">affected applicants</div>
                      </div>
                    </div>

                    {/* Accordion for Before/After Values - Only if there are changes */}
                    {field.beforeAfterValues && field.beforeAfterValues.length > 0 && (
                      <Accordion type="single">
                        <AccordionItem value={`field-${field.key}`} className="border-0">
                          <AccordionTrigger className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-200 flex justify-center">
                            <span>{field.beforeAfterValues.length} change pattern{field.beforeAfterValues.length !== 1 ? 's' : ''}</span>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-3 bg-white border-t border-gray-100">
                            <div className="pt-2">
                              <table className="text-sm table-auto">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th scope="col" className="text-left py-2 pr-4 font-medium text-gray-600">Before</th>
                                    <th scope="col" className="text-left py-2 pr-4 font-medium text-gray-600">After</th>
                                    <th scope="col" className="text-right py-2 pl-4 font-medium text-gray-600">Applicants</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {field.beforeAfterValues.map((change, index) => (
                                    <tr key={index} className="border-b border-gray-100">
                                      <td className="py-2 pr-4">
                                        {change.before !== null ? <div className="font-mono text-sm px-2 py-1 bg-orange-100 border border-orange-200 rounded min-w-6 max-w-fit min-h-7">
                                          {change.before}
                                        </div> : <span className="font-mono text-sm px-1 py-1">
                                          undefined
                                        </span>}
                                      </td>
                                      <td className="py-2 pr-4">
                                        {change.after !== null ? <div className="font-mono text-sm px-2 py-1 bg-blue-100 border border-blue-200 rounded min-w-6 max-w-fit min-h-7">
                                          {change.after}
                                        </div> : <span className="font-mono text-xs px-1 py-1">
                                          undefined
                                        </span>}
                                      </td>
                                      <td className="py-2 text-right pl-4">
                                        <span className="text-base">{change.count}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {field.beforeAfterValues.length >= 100 && (
                                <div className="text-xs text-gray-500">truncated to 100 most common changes</div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    )}
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Target Fields without Changes */}
      {fieldsWithoutChanges.length > 0 && (
        <Card className="max-w-5xl">
          <CardBody>
            <Heading level="h3" className="text-md font-semibold mb-4" id="unchanged-fields">
              Target Fields without Changes
            </Heading>
            <div className="text-sm text-gray-600 mb-3">
              These fields are present in the survey but have no value changes for applicants.
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 list-none">
              {fieldsWithoutChanges
                .map((field) => (
                  <li 
                    key={field.key}
                    className="px-3 py-2 bg-gray-50 rounded text-sm border border-gray-200"
                  >
                    <span className="font-medium text-gray-700">{field.key}</span>
                  </li>
                ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
};
